import { useMemo } from 'react';

import { useBoolean } from '@blockworks/platform/hooks';
import { FlexBox } from '@blockworks/ui/flex-box';
import { SettingsIcon, StarsAltIcon, TrashIcon } from '@blockworks/ui/icon';
import { IconButton } from '@blockworks/ui/icon-button';
import { Menu } from '@blockworks/ui/menu';
import { Popover } from '@blockworks/ui/popover';
import { cn } from '@blockworks/ui/system';
import { Tooltip } from '@blockworks/ui/tooltip';

import { FeatureControlButton } from '../../../feature-control-button';
import { UniversalGritItemActionType, useDashboardGridContext } from '../../context';
import { DRAGGABLE_CANCEL_CLASS } from '../../dashboard-grid.model';

import { MarkdownEditorModal } from './markdown-editor-modal';

type GridItemSettingsProps = { removeOnly?: boolean; itemId?: string };

const GridItemSettings = ({ itemId, removeOnly }: GridItemSettingsProps) => {
    const { getGridItemActions, isEditable, layouts, showAllItems } = useDashboardGridContext();
    const actionMenu = useBoolean(false);
    const editMarkdownModal = useBoolean(false);
    const item = layouts.lg.find(item => item.i === itemId);

    const { actions, onRemove } = useMemo(() => {
        if (item) {
            const actions =
                getGridItemActions?.(item, actionMenu, editMarkdownModal)?.filter(action => action.shouldRender) ?? [];
            if (actions.length > 0) {
                return {
                    actions,
                    item,
                    onRemove: actions.find(action => action.type === UniversalGritItemActionType.Remove)?.onClick,
                };
            }
        }

        return { actions: [], item };
    }, [item, actionMenu, editMarkdownModal, getGridItemActions]);

    if (!isEditable) return null;

    if (removeOnly && onRemove)
        return (
            <IconButton
                classNames={{ base: cn(`${DRAGGABLE_CANCEL_CLASS} pointer-events-auto`) }}
                size="xs"
                aria-label="Remove Item"
                icon={TrashIcon}
                variant="ghost"
                onClick={onRemove}
            />
        );

    if (actions.length < 1) return null;

    return (
        <FlexBox alignItems="center" gap={1}>
            {!!item && (
                <MarkdownEditorModal
                    item={item}
                    open={editMarkdownModal.value}
                    close={editMarkdownModal.setFalse}
                    onOpenChange={editMarkdownModal.setValue}
                />
            )}
            {item?.data.isFeatured && !showAllItems && (
                <Tooltip side="top" align="start" variant="surface">
                    <Tooltip.Trigger className="cursor-default text-primary">
                        <StarsAltIcon
                            aria-label="Featured Visualization"
                            size={4}
                            className={cn(`w-4 ${DRAGGABLE_CANCEL_CLASS} pointer-events-auto cursor-default`)}
                        />
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                        This visualization is featured and
                        <br /> will be visible at the top of the
                        <br /> dashboard for Anonymous users.
                    </Tooltip.Content>
                </Tooltip>
            )}
            <Popover
                open={actionMenu.value}
                onOpenChange={actionMenu.setValue}
                contentProps={{ align: 'start', side: 'left' }}
                trigger={
                    <FeatureControlButton
                        intent="neutral"
                        className={cn(`${DRAGGABLE_CANCEL_CLASS} pointer-events-auto`)}
                        aria-label="options"
                        icon={SettingsIcon}
                        active={actionMenu.value}
                    />
                }
            >
                <Menu orientation="vertical" className={DRAGGABLE_CANCEL_CLASS}>
                    <Menu.List p={1}>
                        {actions.map(props => (
                            <Menu.Item key={String(props.children)} {...props} />
                        ))}
                    </Menu.List>
                </Menu>
            </Popover>
        </FlexBox>
    );
};

export type { GridItemSettingsProps };
export { GridItemSettings };
